import { FC, useState } from 'react'

import { useNavigate } from '@tanstack/react-router'

import { RuleID } from '@forto-dev/insights-engine-models'
import {
  ChannelType,
  CorrelationType,
  motSurvey,
  NotificationPreferenceResponse,
  SurveyQuestionID,
  SurveyResponseID,
  SurveyType,
} from '@forto-dev/notification-center-models'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Stack,
  Typography,
  useToaster,
} from '@forto-dev/phoenix-ui-core'

import { useLocalisation } from '@/utils/localisation'

import { CheckboxSurveyQuestion } from './CheckboxSurveyQuestion'
import { FreeTextSurveyQuestion } from './FreeTextSurveyQuestion'
import { InlineButtonSurveyQuestion } from './InlineButtonSurveyQuestion'
import { RadioSurveyQuestion } from './RadioSurveyQuestion'
import { useNotificationPreferenceUpdate, useSubmitSurveyStep } from '../api'
import {
  SubmitLabel,
  SurveyQuestionChangeEvent,
  SurveyQuestionConfig,
  SurveyQuestionStatus,
} from '../domain/survey/surveyQuestionProps'
import { SurveyQuestionResponse, toUserFeedbackRequest } from '../domain/survey/surveyQuestionResponse'
import { useRenderTranslationKey } from '../hooks/survey/useRenderSurveyText'

type MOTUsefulnessSurveyProps = {
  reference: RuleID
  channelType: ChannelType
  correlationID: string
  correlationType: CorrelationType
}

export const MOTUsefulnessSurvey: FC<MOTUsefulnessSurveyProps> = ({
  reference,
  channelType,
  correlationID,
  correlationType,
}) => {
  const { translate } = useLocalisation()
  const toaster = useToaster()
  const navigate = useNavigate()
  const renderTranslationKey = useRenderTranslationKey()
  const submitSurveyStep = useSubmitSurveyStep()
  const [openSurveyDialog, setOpenSurveyDialog] = useState(false)
  const [currentQuestion, setCurrentQuestion] = useState<SurveyQuestionConfig | undefined>(undefined)
  const [currentResponse, setCurrentResponse] = useState<SurveyQuestionResponse | undefined>(undefined)
  const [canProceed, setCanProceed] = useState<boolean>(false)

  const selectedRuleID = reference
  const updatePreferencesMutation = useNotificationPreferenceUpdate()

  const getRuleIDTranslation = (ruleID: RuleID): string => {
    // @ts-expect-error Type Mismatch between the existing translations and the one below
    return translate(`survey.mot.${ruleID}-title`)
  }

  const unsubscribeFrom = (ruleID: RuleID, options: Parameters<typeof updatePreferencesMutation.mutate>[1]): void => {
    const preferences: NotificationPreferenceResponse[] = [
      {
        reference: ruleID,
        web: false,
        instantEmail: false,
        digestEmail: false,
      },
    ]
    updatePreferencesMutation.mutate({ preferences }, options)
  }

  const handleSubmitStep = (submittedResponse: SurveyQuestionResponse): void => {
    setCanProceed(false)
    submitSurveyStep.mutate({
      context: {
        channelType,
        correlationType,
        correlationID,
      },
      feedback: toUserFeedbackRequest(submittedResponse),
    })
  }

  const q1 = {
    ...motSurvey.questions[SurveyQuestionID.MoTUsefulnessIsItUseful],
    component: InlineButtonSurveyQuestion,
    onSubmit: (state: SurveyQuestionResponse) => {
      handleSubmitStep(state)
      if (state.responses.some((response) => response.id === SurveyResponseID.MoTUsefulnessItIsUseful)) {
        setOpenSurveyDialog(true)
        setCurrentQuestion(q2)
      }
      if (state.responses.some((response) => response.id === SurveyResponseID.MoTUsefulnessItIsNotUseful)) {
        setOpenSurveyDialog(true)
        setCurrentQuestion(q3)
      }
    },
  } satisfies SurveyQuestionConfig

  const q2 = {
    id: SurveyQuestionID.MoTUsefulnessIsItUseful,
    translationKey: 'takeAFewMoments',
    responses: [
      {
        id: SurveyResponseID.MoTUsefulnessItIsUseful,
        translationKey: 'additionalInformation',
      },
    ],
    component: FreeTextSurveyQuestion,
    prefix: (
      <Stack gap={16}>
        <Typography variant="body">{translate('survey.mot.thankYou')}</Typography>
        <Typography variant="body-bold">{translate('survey.mot.tellUsMore')}</Typography>
      </Stack>
    ),
    submitLabel: SubmitLabel.done,
    onSubmit: (state: SurveyQuestionResponse) => {
      handleSubmitStep(state)
      setOpenSurveyDialog(false)
      toaster({
        title: translate('survey.mot.feedbackHasBeenSubmitted'),
        description: translate('survey.mot.feedbackHasBeenSubmittedDescription'),
        variant: 'positive',
      })
    },
  } satisfies SurveyQuestionConfig
  const q3 = {
    ...motSurvey.questions[SurveyQuestionID.MoTUsefulnessWhyIsItNotUseful],
    component: CheckboxSurveyQuestion,
    prefix: (
      <Stack gap={16}>
        <Typography variant="body">
          {translate('survey.mot.thankYou')} {translate('survey.mot.weValueYourFeedback')}
        </Typography>
        <Typography variant="body-bold">{translate('survey.mot.tellUsMore')}</Typography>
      </Stack>
    ),
    submitLabel: SubmitLabel.next,
    onSubmit: (state: SurveyQuestionResponse) => {
      handleSubmitStep(state)

      /**
       * Only shows the option to unsubscribe if the survey is displayed on a user notification
       */
      if (
        correlationType === CorrelationType.UserNotification &&
        state.responses.some(
          (response) =>
            response.id === SurveyResponseID.MoTUsefulnessNotRelevant ||
            response.id === SurveyResponseID.MoTUsefulnessTooMany
        )
      ) {
        setOpenSurveyDialog(true)
        setCurrentQuestion(q4)
      } else {
        setOpenSurveyDialog(false)
        toaster({
          title: translate('survey.mot.feedbackHasBeenSubmitted'),
          description: translate('survey.mot.feedbackHasBeenSubmittedDescription'),
          variant: 'positive',
        })
      }
    },
  } satisfies SurveyQuestionConfig
  const q4 = {
    ...motSurvey.questions[SurveyQuestionID.MoTUsefulnessWhatWouldYouLikeToDo],
    responses: motSurvey.questions[SurveyQuestionID.MoTUsefulnessWhatWouldYouLikeToDo].responses.map((response) => ({
      ...response,
      render: (props) => {
        switch (props.id) {
          case SurveyResponseID.MoTUsefulnessUnsubscribe:
            return (
              <Stack>
                <Typography variant="body-bold">
                  {renderTranslationKey(props.translationKey, {
                    title: getRuleIDTranslation(selectedRuleID),
                  })}
                </Typography>
                <Typography>
                  {`${translate('survey.mot.note')}: ${translate('survey.mot.youWillNotReceiveWarning')}`}
                </Typography>
              </Stack>
            )
          default:
            return renderTranslationKey(props.translationKey)
        }
      },
    })),
    component: RadioSurveyQuestion,
    submitLabel: SubmitLabel.next,
    onSubmit: (state: SurveyQuestionResponse) => {
      handleSubmitStep(state)
      if (state.responses.some((response) => response.id === SurveyResponseID.MoTUsefulnessUnsubscribe)) {
        unsubscribeFrom(selectedRuleID, {
          // Wait until the mutation is successful before closing the dialog and showing success
          onSuccess: () => {
            setOpenSurveyDialog(false)
            toaster({
              title: translate('survey.mot.unsubscribedSuccessfully', {
                title: getRuleIDTranslation(selectedRuleID),
              }),
              variant: 'positive',
            })
          },
          // Allow retry on error
          onError: () => {
            setCanProceed(true)
          },
        })
      } else if (state.responses.some((response) => response.id === SurveyResponseID.MoTUsefulnessConfigure)) {
        // NOTE: this only supports navigating within the domain, to support the manage-notifications case of navigating to a different page, we need to add a new prop to the question config to define the SHIP_BASE_URL
        navigate({ to: '/notification-center/settings' })
        setOpenSurveyDialog(false)
      } else {
        // NOTE: It shouldn't fall into this case
        toaster({
          title: translate('survey.mot.feedbackHasBeenSubmitted'),
          description: translate('survey.mot.feedbackHasBeenSubmittedDescription'),
          variant: 'positive',
        })
        setOpenSurveyDialog(false)
      }
    },
  } satisfies SurveyQuestionConfig

  const onAnswerInitialSurveyQuestion = (event: SurveyQuestionChangeEvent): void => {
    q1.onSubmit(event.surveyQuestionResponse)
  }

  const handleCloseDialog = (): void => {
    setOpenSurveyDialog(false)
  }
  const handleSurveyQuestionResponseUpdate = (response: SurveyQuestionChangeEvent): void => {
    setCurrentResponse(response.surveyQuestionResponse)
    setCanProceed(response.status === SurveyQuestionStatus.answered)
  }

  const handleNext = (): void => {
    if (!currentQuestion || !currentResponse) return
    currentQuestion.onSubmit(currentResponse)
  }

  return (
    <>
      <InlineButtonSurveyQuestion question={q1} onChange={onAnswerInitialSurveyQuestion} />
      <Dialog
        open={openSurveyDialog}
        onOpenChange={() => {
          if (openSurveyDialog) {
            handleCloseDialog()
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{translate(`survey.mot.${SurveyType.MoTUsefulness}`)}</DialogTitle>
          </DialogHeader>
          <Stack>
            {currentQuestion != null ? currentQuestion.prefix : undefined}
            {currentQuestion && (
              <currentQuestion.component question={currentQuestion} onChange={handleSurveyQuestionResponseUpdate} />
            )}
          </Stack>
          <DialogFooter>
            <DialogClose asChild>
              <Button onClick={handleCloseDialog}>{translate('survey.mot.cancel')}</Button>
            </DialogClose>
            <Button onClick={handleNext} variant="primary" disabled={!canProceed} data-testid="survey-submit-button">
              {translate(`survey.mot.${currentQuestion?.submitLabel != null ? currentQuestion.submitLabel : 'next'}`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
